/**
 * Route data API client — thin module that fetches route data from the server.
 *
 * Phase 80.1: Replaces the old barrel-import pattern (detailed-routes.ts + 190 JSON imports)
 * with async fetch from /api/routes/registry and /api/routes/:routeId.
 *
 * Module-level cache ensures sync accessors (getRouteById, getAllRoutes, etc.)
 * work after the registry has been fetched once at app startup.
 */

import { useQuery, useMutation, useQueryClient, type UseQueryResult, type UseMutationResult } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import type { RouteConfig, RouteTier, RouteCategory, RouteFormData, MyRouteEntry } from "@/types/route";

// Re-export the interface (consumers import RouteRegistryEntry from @/route-data)
export interface RouteRegistryEntry {
  id: string;
  name: string;
  shortName: string;
  location: string;
  startPoint: string;
  endPoint: string;
  country: string;
  dates: string[];
  tier: RouteTier;
  category: RouteCategory;
  description?: string;
  distance: string;
  duration: string;
  difficulty?: "easy" | "moderate" | "challenging";
  bestSeason?: string;
  relatedRouteIds?: string[];
  lat?: number;
  lng?: number;
  region?: string;
  fitnessLevel?: "beginner" | "intermediate" | "advanced" | "expert";
  permits?: { required: boolean; details?: string; url?: string };
  guideRequired?: boolean;
  maxAltitude?: number;
  gearLevel?: "light" | "moderate" | "technical" | "mountaineering";
  dailyVisitors?: number;
  permitCap?: number;
  imageUrl?: string;
  operatorHandle?: string | null;
  /** Cross-cutting "what kind of trip" tags. See shared/route-tags.ts for vocabulary. */
  tags?: string[];
}

// --- Module-level cache ---
let registryCache: RouteRegistryEntry[] = [];
let registryLoaded = false;
let registryPromise: Promise<RouteRegistryEntry[]> | null = null;

// --- Async fetchers ---

/**
 * Fetch the full route registry from the server API.
 * Populates the module-level cache on first successful call.
 * Concurrent calls share the same in-flight promise (dedup).
 */
export async function fetchRouteRegistry(): Promise<RouteRegistryEntry[]> {
  if (registryLoaded) return registryCache;
  if (registryPromise) return registryPromise;
  registryPromise = (async () => {
    try {
      const res = await fetch("/api/routes/registry");
      if (!res.ok) throw new Error(`Registry fetch failed: ${res.status}`);
      const json = await res.json();
      registryCache = json.data ?? json;
      registryLoaded = true;
      return registryCache;
    } catch {
      return registryCache;
    } finally {
      registryPromise = null;
    }
  })();
  return registryPromise;
}

/**
 * Fetch a single route's full config (with stopPoints, attractions, logistics, checklist).
 * Returns null if the route doesn't exist or the fetch fails.
 */
export async function fetchRouteDetail(routeId: string): Promise<RouteConfig | null> {
  if (!routeId || !/^[\w-]+$/.test(routeId)) return null;
  try {
    const res = await fetch(`/api/routes/${routeId}`);
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
}

// --- Sync accessors (from module cache) ---

/** Returns the cached registry. Empty array if not yet loaded. */
export function getRegistryCache(): RouteRegistryEntry[] {
  return registryCache;
}

/** Returns the full cached registry array. Alias for getRegistryCache(). */
export function getAllRoutes(): RouteRegistryEntry[] {
  return registryCache;
}

/** Find a single route by ID from the module cache. */
export function getRouteById(routeId: string): RouteRegistryEntry | null {
  return registryCache.find((r) => r.id === routeId) || null;
}

/** Filter routes by country code from the module cache. */
export function getRoutesForCountry(countryCode: string): RouteRegistryEntry[] {
  return registryCache.filter((r) => r.country === countryCode);
}

/** Get routes related to a given route from the module cache. */
export function getRelatedRoutes(routeId: string): RouteRegistryEntry[] {
  const entry = registryCache.find((r) => r.id === routeId);
  if (!entry?.relatedRouteIds) return [];
  return registryCache.filter((r) => entry.relatedRouteIds!.includes(r.id));
}

// --- DEPRECATED sync detail accessor (returns null — use fetchRouteDetail or useRouteDetail) ---
export function getRouteDetailById(_routeId: string): RouteConfig | null {
  return null;
}

/** Returns empty array — detailed route IDs are no longer tracked client-side. */
export function getDetailedRouteIds(): string[] {
  return [];
}

// Backward compat: `registry` named export (live reference to cache array)
export { registryCache as registry };

// --- React Query hooks ---

export interface UseRouteRegistryOptions {
  destination?: string;
}

/** Hook to load the full route registry. Returns cached data while refreshing. */
export function useRouteRegistry(opts?: UseRouteRegistryOptions): UseQueryResult<RouteRegistryEntry[]> {
  const { destination } = opts ?? {};

  const params = new URLSearchParams();
  if (destination) params.set("destination", destination);
  const paramsString = params.toString();
  const url = paramsString ? `/api/routes/registry?${paramsString}` : "/api/routes/registry";

  const paramsObject = { destination };

  return useQuery({
    queryKey: ["/api/routes/registry", paramsObject],
    queryFn: async () => {
      // Public (fix #94) + credentials for stale-session resilience; degrade
      // to [] on 401 so the picker universe never collapses to empty.
      const res = await fetch(url, { credentials: "include" });
      if (res.status === 401) return [];
      if (!res.ok) throw new Error(`Registry fetch failed: ${res.status}`);
      const json = await res.json();
      const data = json.data ?? json;
      // Update module-level cache when fetching without filters
      if (!destination) {
        registryCache.splice(0, registryCache.length, ...data);
        registryLoaded = true;
      }
      return data as RouteRegistryEntry[];
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    placeholderData: (!destination && registryCache.length > 0) ? registryCache : undefined,
  });
}

/** Hook to load a single route's full config on demand. */
export function useRouteDetail(routeId: string | undefined): UseQueryResult<RouteConfig | null> {
  return useQuery({
    queryKey: ["/api/routes/detail", routeId],
    queryFn: () => fetchRouteDetail(routeId!),
    enabled: !!routeId && /^[\w-]+$/.test(routeId),
    staleTime: Infinity,
    gcTime: 60 * 60 * 1000,
  });
}

// ── Route Creator mutation hooks ──

export function useMyRoutes() {
  return useQuery<MyRouteEntry[]>({
    queryKey: ["/api/routes/mine"],
    queryFn: async () => {
      const res = await fetch("/api/routes/mine", { credentials: "include" });
      if (!res.ok) throw new Error(`${res.status}`);
      return res.json();
    },
    staleTime: 60 * 1000,
  });
}

export function useCreateRoute(): UseMutationResult<{ id: string }, Error, RouteFormData> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: RouteFormData) => {
      const res = await apiRequest("POST", "/api/routes", data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/routes/mine"] });
      queryClient.invalidateQueries({ queryKey: ["/api/routes/registry"] });
    },
  });
}

export function useUpdateRoute(
  routeId: string | undefined,
): UseMutationResult<void, Error, Partial<RouteFormData> & { draftStep?: number }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      if (!routeId) throw new Error("No routeId");
      await apiRequest("PATCH", `/api/routes/${routeId}`, data);
    },
    onSuccess: () => {
      if (routeId) {
        queryClient.invalidateQueries({ queryKey: ["/api/routes/detail", routeId] });
        queryClient.invalidateQueries({ queryKey: ["/api/routes/mine"] });
      }
    },
  });
}

export function useDeleteRoute(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (routeId: string) => {
      await apiRequest("DELETE", `/api/routes/${routeId}`, { confirmed: true });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/routes/mine"] });
      queryClient.invalidateQueries({ queryKey: ["/api/routes/registry"] });
    },
  });
}

export function useGenerateRoute(): UseMutationResult<RouteFormData, Error, string> {
  return useMutation({
    mutationFn: async (prompt: string) => {
      const res = await apiRequest("POST", "/api/routes/generate", { prompt });
      return res.json();
    },
  });
}
